import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useCreateDirectConversation } from 'chat/conversations/queries/create_direct'
import { sendDirectMessage } from 'chat/conversations/utils'
import { useCompaniesContext } from 'company/context'
import { TeamMemberScreenProps } from 'home/stack/types'
import { useAddEditUserContext } from 'management_team/context'
import { useManagementTeamActions } from 'management_team/management_actions_context'
import { useUserDetails } from 'management_team/queries/detail'
import { useCallback, useMemo } from 'react'
import { confirmationAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { onCallSMS } from 'shared/utils'
import { useUser } from 'users/queries/detail'
import { TeamMemberView } from './views'

export function TeamMemberScreen({ navigation, route }: TeamMemberScreenProps) {
  const { data: user } = useUser()
  const { handleNavigation } = useRoutingContext()
  const {
    setType,
    setUserId,
    userFields,
    setUserFields,
    role,
    setUserFieldsByUserDetails,
  } = useAddEditUserContext()
  const { getIsUserUpdating, onDeleteUser } = useManagementTeamActions()

  const createDirectConversationMutation = useCreateDirectConversation()
  const { activeCompany } = useCompaniesContext()

  const userDetailsQuery = useUserDetails(
    activeCompany?.id,
    route.params.userId,
    role
  )
  const isCurrentUser = useMemo(
    () => user?.id === userDetailsQuery.data?.id,
    [userDetailsQuery.data?.id, user?.id]
  )

  const onPressCall = useCallback(async () => {
    const number = userDetailsQuery.data?.phoneNumber
    if (!number) {
      return
    }
    await onCallSMS({ title: 'Call', type: 'call', phoneNumber: number })
  }, [userDetailsQuery.data?.phoneNumber])

  const onPressChat = useCallback(async () => {
    await sendDirectMessage(
      navigation,
      createDirectConversationMutation,
      route.params.userId
    )
  }, [createDirectConversationMutation, navigation, route.params.userId])

  const onPressEdit = useCallback(() => {
    setUserId(userDetailsQuery.data?.id)
    setType('editUser')
    setUserFieldsByUserDetails(userDetailsQuery.data)
    handleNavigation({
      routeName: 'add_edit_user',
    })
  }, [
    setUserId,
    userDetailsQuery.data,
    setType,
    setUserFieldsByUserDetails,
    handleNavigation,
  ])

  const onPressRemove = useCallback(async () => {
    const title =
      role === 'observer'
        ? t('confirmDeleteObserverTitle')
        : t('confirmDeleteUserTitle')
    const message =
      role === 'observer'
        ? t('confirmDeleteObserverMessage')
        : t('confirmDeleteUserMessage')
    const confirmed = await confirmationAlert(title, message)
    if (confirmed) {
      if (activeCompany?.id) {
        onDeleteUser(activeCompany.id, route.params.userId)
        handleNavigation({
          routeName: 'management_team',
        })
      }
    }
  }, [
    activeCompany?.id,
    handleNavigation,
    onDeleteUser,
    role,
    route.params.userId,
  ])

  const onPressChooseStores = useCallback(() => {
    setUserId(userDetailsQuery.data?.id)
    setType('editStores')
    setUserFields({
      ...userFields,
      storeIds:
        userDetailsQuery.data?.stores?.map(store => Number(store.id)) ?? [],
    })
    handleNavigation({
      routeName: 'observer_store_chooser',
    })
  }, [
    handleNavigation,
    userDetailsQuery.data?.id,
    userDetailsQuery.data?.stores,
    userFields,
    setUserFields,
    setType,
    setUserId,
  ])

  const onRefresh = useCallback(async () => {
    await userDetailsQuery.refetch()
  }, [userDetailsQuery])

  return (
    <StatusBoundary
      isLoading={userDetailsQuery.isLoading}
      error={userDetailsQuery.error}
    >
      <TeamMemberView
        user={userDetailsQuery.data}
        role={role}
        isCurrentUser={isCurrentUser}
        onPressCall={onPressCall}
        onPressChat={onPressChat}
        onPressEdit={onPressEdit}
        onPressRemove={onPressRemove}
        onPressChooseStores={onPressChooseStores}
        onRefresh={onRefresh}
        lastUpdatedAt={userDetailsQuery.dataUpdatedAt}
        isRefreshing={userDetailsQuery.isFetching}
        isProcessing={getIsUserUpdating(route.params.userId)}
      />
    </StatusBoundary>
  )
}
